import * as React from 'react';
import { cn } from '../../lib/cn';

export type GlassCardVariant = 'default' | 'soft' | 'strong' | 'lg' | 'accent';

const VARIANT_CLASS: Record<GlassCardVariant, string> = {
  default: 'glass',
  soft: 'glass-soft',
  strong: 'glass-strong',
  lg: 'glass-lg',
  accent: 'glass-accent',
};

interface GlassCardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: GlassCardVariant;
}

function GlassCard({
  variant = 'default',
  className,
  children,
  ...props
}: GlassCardProps) {
  return (
    <div className={cn(VARIANT_CLASS[variant], className)} {...props}>
      {children}
    </div>
  );
}

/* ---------------------------------- Card ---------------------------------- */

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-card=""
    className={cn(
      'glass rounded-[16px] transition-shadow duration-200',
      className,
    )}
    {...props}
  />
));
Card.displayName = 'Card';

/* ------------------------------- CardHeader ------------------------------- */

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-card-header=""
    className={cn('flex items-start justify-between gap-4 p-6', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

/* -------------------------------- CardTitle ------------------------------- */

const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight text-text-primary',
      className,
    )}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

/* ----------------------------- CardDescription ---------------------------- */

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-gray-500', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

/* ------------------------------- CardContent ------------------------------ */

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-card-content=""
    className={cn('p-6 pt-0', className)}
    {...props}
  />
));
CardContent.displayName = 'CardContent';

/* ------------------------------- CardFooter ------------------------------- */

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-6 pt-0', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

/* ----------------------------- CardRefreshButton ---------------------------- */

interface CardRefreshButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading?: boolean;
}

const CardRefreshButton = React.forwardRef<
  HTMLButtonElement,
  CardRefreshButtonProps
>(({ isLoading, className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      'inline-flex items-center justify-center rounded-none p-2',
      isLoading && 'animate-spin',
      className,
    )}
    {...props}
  >
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  </button>
));
CardRefreshButton.displayName = 'CardRefreshButton';

export {
  GlassCard,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  CardRefreshButton,
};
export type { GlassCardProps, CardRefreshButtonProps };
